export type GridShadeDirection = 'G' | 'L';

export interface GridShadePoint {
  x: number;
  y: number;
}

export interface GridGraphPoint {
  cx: number;
  cy: number;
  [key: string]: unknown;
}

export interface GridShadeObject {
  t?: string;
  first?: GridGraphPoint;
  second?: GridGraphPoint;
  shade?: GridShadeDirection;
  [key: string]: unknown;
}

export interface GridShadeError {
  msg: string;
  top: string;
}

export interface GridShadeBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface ApplyGridShadePointOptions {
  objects?: GridShadeObject[];
  permanentObjects?: GridShadeObject[];
  shadePoint: GridShadePoint;
  firstQuadrant?: boolean;
  fillWithDots?: boolean;
  width?: number;
  height?: number;
  pixelsPerBlock?: number;
  preserveExistingShade?: boolean;
  validateShadeGeometry?: boolean;
  bounds?: GridShadeBounds;
  shadePointSearchStep?: number;
  clearHighlightDots?: () => void;
  addHighlightDot?: (x: number, y: number) => void;
  reportError?: (error: GridShadeError) => void;
}

export interface ApplyGridShadePointResult {
  hasLineRayOrSegment: boolean;
  shadePoint?: GridShadePoint;
  error?: GridShadeError;
}

export interface ClearGridShadeOptions {
  userObjects?: GridShadeObject[];
  permanentObjects?: GridShadeObject[];
  hadShadePoint?: boolean;
}

export interface GridShadePointGrid {
  args: {
    o?: {
      u?: GridShadeObject[];
      p?: GridShadeObject[];
    };
    eF?: unknown;
    pixelsPerBlock?: number;
    shadePoint?: GridShadePoint;
  };
  width?: number;
  height?: number;
  shadePoint?: GridShadePoint;
}

export interface SetGridShadePointOptions {
  grid: GridShadePointGrid;
  shadePoint: GridShadePoint;
  fillWithDots?: boolean;
  preserveExistingShade?: boolean;
  validateShadeGeometry?: boolean;
  getBounds?: () => GridShadeBounds;
  clearHighlightDots?: () => void;
  addHighlightDot?: (x: number, y: number) => void;
  reportError?: (error: GridShadeError) => void;
}

interface ShadedGridDrag<State> {
  moved: boolean;
  startState: State;
}

interface ShadedGridDragController<State> {
  refreshGridShadeAfterDrag: (
    validateShadeGeometry?: boolean
  ) => 'invalid' | undefined;
  applyGridStateFromHistory: (state: State) => void;
}

const SHADEABLE_TYPES = new Set(['Line', 'Segment', 'Ray']);

const isDot = (obj: GridShadeObject): boolean => obj.t === 'dot';

const isShadeableLineObject = (obj: GridShadeObject): boolean =>
  Boolean(obj.t && SHADEABLE_TYPES.has(obj.t));

const getShadeObjects = (
  objects: GridShadeObject[] | undefined,
  permanentObjects: GridShadeObject[] | undefined
): GridShadeObject[] | undefined =>
  objects && objects.length > 0 ? objects : permanentObjects;

const countNonDotObjects = (objects: GridShadeObject[] | undefined): number =>
  objects ? objects.filter(obj => !isDot(obj)).length : 0;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const clampShadePoint = (
  point: GridShadePoint,
  bounds?: GridShadeBounds
): GridShadePoint => {
  if (!bounds) {
    return point;
  }

  return {
    x: clamp(point.x, bounds.minX, bounds.maxX),
    y: clamp(point.y, bounds.minY, bounds.maxY),
  };
};

const reportInvalidShade = (
  error: GridShadeError,
  reportError?: (error: GridShadeError) => void
): ApplyGridShadePointResult => {
  reportError?.(error);

  return {
    hasLineRayOrSegment: false,
    error,
  };
};

const validateShadeObjects = (
  objects: GridShadeObject[] | undefined,
  firstQuadrant: boolean,
  reportError?: (error: GridShadeError) => void
): ApplyGridShadePointResult | undefined => {
  if (countNonDotObjects(objects) > 5) {
    return reportInvalidShade(
      {
        msg:
          'You cannot use the shade tool with more than five objects on your graph.',
        top: 'Invalid Shade Attempt',
      },
      reportError
    );
  }

  if (!objects || objects.length === 0) {
    return undefined;
  }

  for (const obj of objects) {
    if (obj.t === 'dot' || obj.t === 'Parabola' || obj.t === 'Shade') {
      continue;
    }

    if (!firstQuadrant && (obj.t === 'Ray' || obj.t === 'Segment')) {
      return reportInvalidShade(
        {
          msg:
            'You cannot use the shade tool with anything other than lines in a four-quadrant graph.',
          top: 'Invalid Shade Attempt',
        },
        reportError
      );
    }

    if (!firstQuadrant) {
      continue;
    }

    const margin = 5;

    if (obj.t === 'Segment' && obj.first && obj.second) {
      const firstOnYAxis = Math.abs(obj.first.cx) <= margin;
      const firstOnXAxis = Math.abs(obj.first.cy) <= margin;
      const secondOnYAxis = Math.abs(obj.second.cx) <= margin;
      const secondOnXAxis = Math.abs(obj.second.cy) <= margin;
      const validDifferentAxes =
        (firstOnYAxis && secondOnXAxis) || (firstOnXAxis && secondOnYAxis);
      const touchesOrigin =
        (firstOnYAxis && firstOnXAxis) || (secondOnYAxis && secondOnXAxis);

      if (touchesOrigin) {
        return reportInvalidShade(
          {
            msg:
              'To shade with a segment, neither endpoint can be at the origin.',
            top: 'Invalid Shade Attempt',
          },
          reportError
        );
      }

      if (!validDifferentAxes) {
        return reportInvalidShade(
          {
            msg:
              'To shade with a segment, place one endpoint on the y-axis and the other on the x-axis.',
            top: 'Invalid Shade Attempt',
          },
          reportError
        );
      }
    }

    if (obj.t === 'Ray' && obj.first) {
      if (
        !(Math.abs(obj.first.cx) <= margin || Math.abs(obj.first.cy) <= margin)
      ) {
        return reportInvalidShade(
          {
            msg:
              'When shading with rays, the endpoint of the each ray should be on one of the axes.',
            top: 'Invalid Shade Attempt',
          },
          reportError
        );
      }
    }
  }

  return undefined;
};

export const getShadeDirectionForPoint = (
  obj: GridShadeObject,
  shadePoint: GridShadePoint,
  options?: {
    existingShade?: GridShadeDirection;
    preserveExistingShade?: boolean;
    verticalThreshold?: number;
  }
): GridShadeDirection | undefined => {
  if (options?.preserveExistingShade && options.existingShade) {
    return options.existingShade;
  }

  if (!obj.first || !obj.second) {
    return undefined;
  }

  const verticalThreshold = options?.verticalThreshold ?? 3;

  if (Math.abs(obj.first.cx - obj.second.cx) <= verticalThreshold) {
    return shadePoint.x <= obj.first.cx ? 'L' : 'G';
  }

  const slope = (obj.first.cy - obj.second.cy) / (obj.first.cx - obj.second.cx);
  const intercept = obj.first.cy - slope * obj.first.cx;
  const lineYAtShadePoint = slope * shadePoint.x + intercept;

  return lineYAtShadePoint >= shadePoint.y ? 'L' : 'G';
};

const pointMatchesExistingShade = (
  obj: GridShadeObject,
  shadePoint: GridShadePoint,
  verticalThreshold: number
): boolean => {
  if (!obj.shade) {
    return true;
  }

  return (
    getShadeDirectionForPoint(obj, shadePoint, {
      verticalThreshold,
    }) === obj.shade
  );
};

const pointMatchesAllExistingShades = (
  objects: GridShadeObject[],
  shadePoint: GridShadePoint,
  verticalThreshold: number
): boolean =>
  objects.every(obj =>
    pointMatchesExistingShade(obj, shadePoint, verticalThreshold)
  );

const nudgeShadePointForObject = (
  obj: GridShadeObject,
  shadePoint: GridShadePoint,
  bounds: GridShadeBounds | undefined,
  verticalThreshold: number
): GridShadePoint => {
  if (!obj.first || !obj.second || !obj.shade) {
    return shadePoint;
  }

  const margin = 1;

  if (Math.abs(obj.first.cx - obj.second.cx) <= verticalThreshold) {
    return clampShadePoint(
      {
        x: obj.first.cx + (obj.shade === 'L' ? -margin : margin),
        y: shadePoint.y,
      },
      bounds
    );
  }

  const slope = (obj.first.cy - obj.second.cy) / (obj.first.cx - obj.second.cx);
  const intercept = obj.first.cy - slope * obj.first.cx;
  const lineYAtShadePoint = slope * shadePoint.x + intercept;

  return clampShadePoint(
    {
      x: shadePoint.x,
      y: lineYAtShadePoint + (obj.shade === 'L' ? -margin : margin),
    },
    bounds
  );
};

const findNearestMatchingShadePoint = (
  objects: GridShadeObject[],
  shadePoint: GridShadePoint,
  bounds: GridShadeBounds | undefined,
  searchStep: number,
  verticalThreshold: number
): GridShadePoint | undefined => {
  if (!bounds) {
    return undefined;
  }

  const step = Math.max(1, Math.round(searchStep));
  let nearestPoint: GridShadePoint | undefined;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (let x = bounds.minX; x <= bounds.maxX; x += step) {
    for (let y = bounds.minY; y <= bounds.maxY; y += step) {
      const candidate = { x, y };

      if (
        !pointMatchesAllExistingShades(objects, candidate, verticalThreshold)
      ) {
        continue;
      }

      const distance = Math.hypot(
        candidate.x - shadePoint.x,
        candidate.y - shadePoint.y
      );

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestPoint = candidate;
      }
    }
  }

  return nearestPoint;
};

export const getAdjustedShadePointForExistingShades = ({
  objects,
  shadePoint,
  bounds,
  searchStep = 5,
  verticalThreshold = 3,
}: {
  objects: GridShadeObject[] | undefined;
  shadePoint: GridShadePoint;
  bounds?: GridShadeBounds;
  searchStep?: number;
  verticalThreshold?: number;
}): GridShadePoint => {
  const shadeObjects = objects
    ? objects.filter(
        obj =>
          isShadeableLineObject(obj) && obj.first && obj.second && obj.shade
      )
    : [];

  if (
    shadeObjects.length === 0 ||
    pointMatchesAllExistingShades(shadeObjects, shadePoint, verticalThreshold)
  ) {
    return shadePoint;
  }

  let candidate = clampShadePoint({ ...shadePoint }, bounds);
  const maxIterations = Math.max(1, shadeObjects.length * 4);

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let changed = false;

    for (const obj of shadeObjects) {
      if (pointMatchesExistingShade(obj, candidate, verticalThreshold)) {
        continue;
      }

      const nextCandidate = nudgeShadePointForObject(
        obj,
        candidate,
        bounds,
        verticalThreshold
      );

      changed =
        changed ||
        nextCandidate.x !== candidate.x ||
        nextCandidate.y !== candidate.y;
      candidate = nextCandidate;
    }

    if (
      pointMatchesAllExistingShades(shadeObjects, candidate, verticalThreshold)
    ) {
      return candidate;
    }

    if (!changed) {
      break;
    }
  }

  return (
    findNearestMatchingShadePoint(
      shadeObjects,
      shadePoint,
      bounds,
      searchStep,
      verticalThreshold
    ) || shadePoint
  );
};

const buildFillWithDotsCandidates = (
  width: number,
  height: number,
  pixelsPerBlock: number
): number[][] => {
  const maybes: number[][] = [];

  for (let x = 0; x <= width; x += pixelsPerBlock) {
    for (let y = 0; y <= height; y += pixelsPerBlock) {
      maybes.push([x, y]);
    }
  }

  return maybes;
};

const addFillWithDotsHighlightDots = (
  obj: GridShadeObject,
  direction: GridShadeDirection,
  maybes: number[][],
  addHighlightDot: (x: number, y: number) => void
): void => {
  if (!obj.first || !obj.second) {
    return;
  }

  if (obj.first.cx === obj.second.cx) {
    maybes.forEach(([x, y]) => {
      if (direction === 'L' && x <= obj.first!.cx) {
        addHighlightDot(x, y);
      } else if (direction === 'G' && x >= obj.first!.cx) {
        addHighlightDot(x, y);
      }
    });

    return;
  }

  const slope = (obj.first.cy - obj.second.cy) / (obj.first.cx - obj.second.cx);
  const intercept = obj.first.cy - slope * obj.first.cx;

  maybes.forEach(([x, y]) => {
    const lineY = slope * x + intercept;

    if (direction === 'L' && y <= lineY) {
      addHighlightDot(x, y);
    } else if (direction === 'G' && y >= lineY) {
      addHighlightDot(x, y);
    }
  });
};

export const applyGridShadePoint = ({
  objects,
  permanentObjects,
  shadePoint,
  firstQuadrant = false,
  fillWithDots = false,
  width = 0,
  height = 0,
  pixelsPerBlock = 1,
  preserveExistingShade = false,
  validateShadeGeometry = true,
  bounds,
  shadePointSearchStep,
  clearHighlightDots,
  addHighlightDot,
  reportError,
}: ApplyGridShadePointOptions): ApplyGridShadePointResult => {
  const invalidResult = validateShadeGeometry
    ? validateShadeObjects(objects, firstQuadrant, reportError)
    : undefined;

  if (invalidResult) {
    return invalidResult;
  }

  const shadeObjects = getShadeObjects(objects, permanentObjects);
  let hasLineRayOrSegment = false;
  const fillWithDotsCandidates =
    fillWithDots && addHighlightDot
      ? buildFillWithDotsCandidates(width, height, pixelsPerBlock)
      : undefined;
  const verticalThreshold = fillWithDots ? 0 : 3;

  if (shadeObjects) {
    shadeObjects.forEach(obj => {
      if (!isShadeableLineObject(obj)) {
        return;
      }

      hasLineRayOrSegment = true;

      if (!obj.second) {
        return;
      }

      const direction = getShadeDirectionForPoint(obj, shadePoint, {
        existingShade: obj.shade,
        preserveExistingShade,
        verticalThreshold,
      });

      if (!direction) {
        return;
      }

      obj.shade = direction;

      if (fillWithDots && fillWithDotsCandidates && addHighlightDot) {
        clearHighlightDots?.();
        addFillWithDotsHighlightDots(
          obj,
          direction,
          fillWithDotsCandidates,
          addHighlightDot
        );
      }
    });
  }

  return {
    hasLineRayOrSegment,
    shadePoint: preserveExistingShade
      ? getAdjustedShadePointForExistingShades({
          objects: shadeObjects,
          shadePoint,
          bounds,
          searchStep: shadePointSearchStep ?? pixelsPerBlock / 4,
          verticalThreshold,
        })
      : shadePoint,
  };
};

export const setGridShadePoint = ({
  grid,
  shadePoint,
  fillWithDots = false,
  preserveExistingShade = false,
  validateShadeGeometry = true,
  getBounds,
  clearHighlightDots,
  addHighlightDot,
  reportError,
}: SetGridShadePointOptions): 'invalid' | undefined => {
  const result = applyGridShadePoint({
    objects: grid.args.o?.u,
    permanentObjects: grid.args.o?.p,
    shadePoint,
    firstQuadrant: Boolean(grid.args.eF),
    fillWithDots,
    width: grid.width,
    height: grid.height,
    pixelsPerBlock: grid.args.pixelsPerBlock,
    preserveExistingShade,
    validateShadeGeometry,
    bounds: preserveExistingShade ? getBounds?.() : undefined,
    shadePointSearchStep:
      grid.args.pixelsPerBlock === undefined
        ? undefined
        : grid.args.pixelsPerBlock / 4,
    clearHighlightDots,
    addHighlightDot,
    reportError,
  });

  if (result.error) {
    return 'invalid';
  }

  if (result.hasLineRayOrSegment) {
    const nextShadePoint = result.shadePoint || shadePoint;

    grid.shadePoint = nextShadePoint;
    grid.args.shadePoint = nextShadePoint;
  }

  return undefined;
};

export const rollbackInvalidShadedGridDrag = <State>(
  drag: ShadedGridDrag<State>,
  controller: ShadedGridDragController<State>
): boolean => {
  if (!drag.moved || controller.refreshGridShadeAfterDrag(true) !== 'invalid') {
    return false;
  }

  controller.applyGridStateFromHistory(drag.startState);
  drag.moved = false;

  return true;
};

export const clearGridShade = ({
  userObjects,
  permanentObjects,
  hadShadePoint,
}: ClearGridShadeOptions): void => {
  if (userObjects) {
    for (let index = 0; index < userObjects.length; index++) {
      const obj = userObjects[index];

      obj.shade = undefined;

      if (hadShadePoint && obj.t === 'dot') {
        userObjects.splice(index, 1);
        index--;
      }
    }
  }

  permanentObjects?.forEach(obj => {
    obj.shade = undefined;
  });
};
